import React, { Fragment } from 'react';
import { Svg } from '@react-pdf/renderer';
import { MuiCeiling, MuiPartitions, MuiLoadingDocks, MuiStorage, MuiDevices, MuiDoor, MuiFurniture } from './floorPlanElements';

/**
 * Interface for bounding box dimensions
 */
interface BoundingBox {
  width: number;
  height: number;
  [key: string]: any;
}

/**
 * Interface for constants prop
 */
interface Constants {
  reportPlotElements: string[];
  [key: string]: any;
}

/**
 * Interface for MuiFloorPlan component props
 */
interface MuiFloorPlanProps {
  bbox: BoundingBox;
  inContainer?: boolean;
  scale?: number;
  constants: Constants;
  [key: string]: any; // For any additional props that might be passed to child components
}

/**
 * Type for the mapping of element names to components
 */
type FloorPlanElementsMap = {
  [key: string]: React.ComponentType<any>;
};

/**
 * MUI FloorPlan section component that replaces the styled-components FloorPlan section
 * 
 * This component provides a Material UI equivalent of the original FloorPlan section component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiFloorPlanProps} props - Component props
 * @param {BoundingBox} props.bbox - Bounding box dimensions
 * @param {boolean} props.inContainer - Whether the floor plan is in a container
 * @param {number} props.scale - Scale factor for the floor plan
 * @returns {JSX.Element} MUI FloorPlan section component
 */
const MuiFloorPlan: React.FC<MuiFloorPlanProps> = (props) => {
  const { bbox, constants: { reportPlotElements } } = props;
  
  // Each coverage plot page (props.device, set by MuiPlot/Export.tsx to 'microphones' /
  // 'speakers' / 'cameras') only shows the icons for its own device type — e.g. the
  // MICROPHONE COVERAGE page shows mics only, SPEAKER COVERAGE shows speakers only, and
  // CAMERA COVERAGE shows cameras only. MuiDevices requires an explicit `device` prop — it
  // cannot infer the type from context — so we pass through whichever one matches this page.
  const MuiAllDevices: React.FC<any> = (deviceProps) => (
    <MuiDevices {...deviceProps} device={deviceProps.device} />
  );

  // Map of element names to MUI components
  const muiFloorPlanElements: FloorPlanElementsMap = {
    ceiling: MuiCeiling,
    partitions: MuiPartitions,
    loadingDocks: MuiLoadingDocks,
    storage: MuiStorage,
    devices: MuiAllDevices,
    door: MuiDoor,
    furniture: MuiFurniture,
  };
  
  return (
    <Fragment>
      <Svg width={bbox.width} height={bbox.height} >
        {reportPlotElements.map((element, index) => {
          const Element = muiFloorPlanElements[element.toLowerCase()];
          if (!Element) {
            console.warn(`MUI floor plan element "${element}" not found`);
            return null;
          }
          return (
            <Element key={`${element}-${index}`} {...props} />
          );
        })}
      </Svg>
    </Fragment>
  );
};

export default MuiFloorPlan;
